import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Divider, ListItemText, Menu, PopoverOrigin, Typography } from '@mui/material';

import MenuItem from '@/components/MenuItem';

import { Property } from './models/Property';

interface FilterMenuProps {
  properties: Array<Property>;
  numPropertiesVisible?: number;
  anchorEl?: HTMLElement;
  anchorOrigin?: PopoverOrigin;
  onClick?: (property: Property) => void;
  onClose?: () => void;
}

const FilterMenu = (props: FilterMenuProps) => {
  const {
    properties,
    numPropertiesVisible = 4,
    anchorEl,
    anchorOrigin = {
      vertical: 'bottom',
      horizontal: 'left',
    },
    onClick,
    onClose,
  } = props;

  const [expanded, setExpanded] = useState(false);

  const handleOnClick = useCallback(
    (property: Property) => {
      setTimeout(() => {
        onClick?.(property);
      }, 50); // this is to avoid false-trigger of FilterPopper's click away listener
    },
    [onClick]
  );

  const visibleProperties = expanded ? properties : properties.slice(0, numPropertiesVisible);
  const showToggleExpandedButton = properties.length > numPropertiesVisible;

  const toggleExpandedButton = expanded ? (
    <MenuItem onClick={() => setExpanded(false)}>
      <ListItemText
        primary={<FormattedMessage defaultMessage="Less Filters" />}
        primaryTypographyProps={{ color: 'textSecondary' }}
      />
    </MenuItem>
  ) : (
    <MenuItem onClick={() => setExpanded(true)}>
      <ListItemText
        primary={<FormattedMessage defaultMessage="More Filters" />}
        primaryTypographyProps={{ color: 'textSecondary' }}
      />
      <ExpandMoreIcon />
    </MenuItem>
  );

  return (
    <Menu
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            maxHeight: 304,
            maxWidth: 220,
          },
        },
      }}
    >
      <div>
        {visibleProperties.map((property) => (
          <MenuItem
            key={property.name}
            value={property.name}
            onClick={() => handleOnClick(property)}
          >
            <Typography noWrap>{property.label}</Typography>
          </MenuItem>
        ))}
        {showToggleExpandedButton ? <Divider /> : null}
        {showToggleExpandedButton ? toggleExpandedButton : null}
      </div>
    </Menu>
  );
};

export { FilterMenuProps };
export default FilterMenu;
